import { useMemo, useState } from 'react';
import { Drawer } from '../../design-system/Overlay';
import { Pill } from '../../design-system/primitives';
import { Empty, errorView, Loading } from '../../states/States';
import { useLoad } from '../../views/useLoad';
import { useAppServices } from '../../views/ViewContext';
import { CommandError } from './TenantPage';
import { errorMessage, forUsers, text, type CompositionLayer, type LocalizedText } from './tenant-data';

/**
 * Paramètres du tenant (ADR-081), dans la page Tenant (ADR-128 §1) : une carte par paramètre, rangée
 * dans sa section (déclarée par le paramètre, sinon celle de sa couche). La liste des sections est à
 * gauche, les cartes de la section choisie à droite ; une recherche parcourt toutes les sections. Chaque
 * carte montre valeur, badge « Par défaut » ou « Modifié » et couche qui déclare ; la modification (raison
 * obligatoire, retour au défaut) et l'historique s'ouvrent dans un panneau à droite (demandes de
 * l'utilisateur du 2026-10-14). Toute écriture passe par `SetTenantParameter`, autorisée par le serveur.
 */
interface ParameterRow {
  parameter_id: string;
  declared_by: { id: string; version: string };
  label: LocalizedText;
  description?: LocalizedText;
  value_type: 'number' | 'quantity' | 'boolean' | 'string' | 'enum' | 'enum_list';
  section?: string;
  unit?: string;
  enum_values?: string[];
  /** Libellé de chaque valeur (`parameter-body` 1.2.0) : l'écran montre le libellé, jamais le code stocké. */
  enum_labels?: Record<string, LocalizedText>;
  min?: number;
  max?: number;
  default: unknown;
  value: unknown;
  is_default: boolean;
  value_version: number;
  reason?: string;
  set_at?: string;
  history?: Array<{ value: unknown; previous_value?: unknown; value_version: number; reason: string; occurred_at: string }>;
}

type Section = { title: string; hint: string; rows: ParameterRow[] };

const SECTIONS: ReadonlyArray<{ id: string; title: string; hint: string }> = [
  { id: 'general', title: 'Général', hint: 'fuseau, calendrier, devises' },
  { id: 'security', title: 'Sécurité', hint: 'accès et composants' },
  { id: 'operations', title: 'Opérations', hint: 'actions de masse' },
  { id: 'logistics', title: 'Logistique', hint: 'unités logistiques' },
  { id: 'treasury', title: 'Trésorerie', hint: 'rapprochement et prévision' },
  { id: 'quality', title: 'Qualité', hint: 'métrologie' },
];

/** Valeur lisible : une valeur d'énumération montre son libellé déclaré, à défaut son code (code ISO d'une devise). */
export function shown(value: unknown, unit?: string, labels?: Readonly<Record<string, LocalizedText>>): string {
  const option = (entry: unknown): string => (typeof entry === 'string' && labels?.[entry] !== undefined ? text(labels[entry], entry) : String(entry));
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map(option).join(', ');
  if (value !== null && typeof value === 'object' && 'value' in (value as Record<string, unknown>)) return `${String((value as { value: unknown }).value)} ${unit ?? String((value as { unit?: unknown }).unit ?? '')}`.trim();
  if (typeof value === 'boolean') return value ? 'oui' : 'non';
  if (value === undefined || value === null || value === '') return '—';
  return option(value);
}

/** Couche qui déclare un paramètre : le Core, ou le Pack dont l'identifiant préfixe l'artefact (food_meat.parameter.…). */
function layerOf(row: ParameterRow, layers: readonly CompositionLayer[]): { key: string; title: string } {
  const prefix = row.declared_by.id.split('.')[0] ?? '';
  if (prefix === 'core') return { key: 'core', title: 'Core' };
  const layer = layers.find((candidate) => candidate.lineage.replace(/^pack:/, '').replace(/-/g, '_') === prefix);
  return { key: prefix, title: layer === undefined ? prefix : `${text(layer.label, prefix)} ${layer.version}` };
}

export function ParametersTab({ onSaved }: { onSaved: () => void }) {
  const services = useAppServices();
  const [loaded, reload] = useLoad<ParameterRow[]>('tenant-parameters', async () => (await services.client.query<{ parameters: ParameterRow[] }>('GetTenantParameters')).data.parameters);
  // La composition nomme la couche qui déclare ; sans elle, l'identifiant du Pack suffit.
  const [composition] = useLoad<CompositionLayer[]>('tenant-composition', async () => (await services.client.query<{ layers: CompositionLayer[] }>('GetTenantComposition')).data.layers);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const layers = composition.status === 'ready' ? composition.value : [];
  const sections = useMemo((): Array<[string, Section]> => {
    if (loaded.status !== 'ready') return [];
    const groups = new Map<string, Section>();
    for (const section of SECTIONS) groups.set(section.id, { title: section.title, hint: section.hint, rows: [] });
    for (const row of loaded.value) {
      const layer = layerOf(row, layers);
      const key = row.section ?? (layer.key === 'core' ? 'general' : `layer:${layer.key}`);
      if (!groups.has(key)) groups.set(key, { title: SECTIONS.find((section) => section.id === key)?.title ?? layer.title.replace(/\s[\d.]+$/, ''), hint: key.startsWith('layer:') ? 'déclarés par le Pack' : '', rows: [] });
      groups.get(key)!.rows.push(row);
    }
    return [...groups.entries()].filter(([, group]) => group.rows.length > 0);
  }, [loaded, layers]);
  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  if (loaded.value.length === 0 || sections.length === 0) return <Empty label="Aucun paramètre déclaré." />;
  const modified = loaded.value.filter((row) => !row.is_default).length;
  const needle = search.trim().toLowerCase();
  const matches = (row: ParameterRow): boolean => `${text(row.label)} ${text(row.description)} ${row.parameter_id} ${Object.values(row.enum_labels ?? {}).map((label) => text(label)).join(' ')}`.toLowerCase().includes(needle);
  // Une recherche parcourt toutes les sections ; sinon, la section choisie (la première par défaut).
  const current = sections.find(([key]) => key === picked) ?? sections[0]!;
  const visible: Array<[string, Section]> = needle === '' ? [current] : sections.map(([key, group]): [string, Section] => [key, { ...group, rows: group.rows.filter(matches) }]).filter(([, group]) => group.rows.length > 0);
  const selected = editing === null ? undefined : loaded.value.find((row) => row.parameter_id === editing);
  return (
    <div className="ay-tenant__stack">
      <div className="ay-tenant__toolbar">
        <label className="ay-tenant__search">
          <span className="ay-visually-hidden">Rechercher un paramètre</span>
          <input type="search" placeholder="Rechercher dans toutes les sections" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <span className="ay-text ay-text--muted">
          {loaded.value.length} paramètres, {modified === 0 ? 'tous à leur valeur par défaut' : `${modified} modifié${modified > 1 ? 's' : ''}`}
        </span>
      </div>
      <div className="ay-tenant__parameters">
        <nav className="ay-tenant__sections" aria-label="Sections des paramètres">
          <ul>
            {sections.map(([key, group]) => {
              const changed = group.rows.filter((row) => !row.is_default).length;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="ay-tenant__sectionlink"
                    aria-current={needle === '' && key === current[0] ? 'true' : undefined}
                    onClick={() => {
                      setPicked(key);
                      setSearch('');
                    }}
                  >
                    <span>{group.title}</span>
                    <span className="ay-tenant__count">
                      {group.rows.length}
                      {changed > 0 && <span className="ay-tenant__changed"> · {changed} modifié{changed > 1 ? 's' : ''}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="ay-tenant__sectionbody">
          {visible.length === 0 && <Empty label="Aucun paramètre ne correspond à la recherche." />}
          {visible.map(([key, group]) => (
            <section key={key} className="ay-tenant__section" aria-labelledby={`ay-parameters-${key}`}>
              <div className="ay-tenant__sectionhead">
                <h2 id={`ay-parameters-${key}`}>{group.title}</h2>
                {group.hint !== '' && <span className="ay-text ay-text--muted">{group.hint}</span>}
              </div>
              <div className="ay-tenant__cards">
                {group.rows.map((row) => (
                  <ParameterCard key={row.parameter_id} row={row} layer={layerOf(row, layers).title} open={editing === row.parameter_id} onOpen={() => setEditing(row.parameter_id)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
      <Drawer title={selected === undefined ? 'Paramètre' : text(selected.label, selected.parameter_id)} open={selected !== undefined} onClose={() => setEditing(null)} modal>
        {selected !== undefined && (
          <ParameterPanel
            key={`${selected.parameter_id}:${selected.value_version}`}
            row={selected}
            layer={layerOf(selected, layers).title}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              reload();
              onSaved();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

/** Valeur saisie → valeur de commande, selon le type déclaré ; `null` si la saisie n'est pas lisible. */
function parsed(row: ParameterRow, input: string, list: readonly string[]): unknown {
  if (row.value_type === 'enum_list') return [...list];
  if (row.value_type === 'boolean') return input === 'true';
  if (row.value_type === 'string' || row.value_type === 'enum') return input;
  const number = Number(input.replace(',', '.'));
  if (input.trim() === '' || !Number.isFinite(number)) return null;
  return row.value_type === 'quantity' ? { value: number, unit: row.unit } : number;
}

function ParameterCard({ row, layer, open, onOpen }: { row: ParameterRow; layer: string; open: boolean; onOpen: () => void }) {
  const label = text(row.label, row.parameter_id);
  return (
    <article className={`ay-card ay-tenant__parameter${open ? ' ay-tenant__parameter--open' : ''}`} aria-label={label}>
      <div className="ay-tenant__cardhead">
        <strong className="ay-tenant__grow">{label}</strong>
        <Pill tone={row.is_default ? 'neutral' : 'warn'}>{row.is_default ? 'Par défaut' : 'Modifié'}</Pill>
      </div>
      {row.description !== undefined && <p className="ay-tenant__description">{forUsers(text(row.description))}</p>}
      <div className="ay-tenant__valueline">
        <span className="ay-tenant__value">{shown(row.value, row.unit, row.enum_labels)}</span>
        {!row.is_default && <span className="ay-text ay-text--muted">défaut : {shown(row.default, row.unit, row.enum_labels)}</span>}
        <span className="ay-tenant__grow" />
        <button type="button" className="ay-button ay-button--ghost" aria-haspopup="dialog" onClick={onOpen}>
          Modifier
        </button>
      </div>
      <div className="ay-tenant__footnote">
        <span>Déclaré par : {layer}</span>
        {row.set_at !== undefined && (
          <>
            <span>· modifié le {new Date(row.set_at).toLocaleDateString('fr-FR')}</span>
            <button type="button" className="ay-tenant__linkbutton" aria-haspopup="dialog" onClick={onOpen}>
              Historique
            </button>
          </>
        )}
      </div>
    </article>
  );
}

/** Panneau à droite : valeur actuelle, modification (motif obligatoire, retour au défaut), historique. */
function ParameterPanel({ row, layer, onCancel, onSaved }: { row: ParameterRow; layer: string; onCancel: () => void; onSaved: () => void }) {
  const services = useAppServices();
  const initial = row.value_type === 'quantity' ? String((row.value as { value?: unknown } | null)?.value ?? '') : Array.isArray(row.value) ? '' : String(row.value ?? '');
  const [input, setInput] = useState(initial);
  const [list, setList] = useState<string[]>(Array.isArray(row.value) ? (row.value as string[]) : []);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ message: string; elevation: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async (value: unknown): Promise<void> => {
    if (value === null) return setError({ message: 'Valeur illisible.', elevation: false });
    setBusy(true);
    setError(null);
    try {
      await services.client.command('SetTenantParameter', { payload: { parameter_id: row.parameter_id, value, reason: reason.trim(), ...(row.is_default ? {} : { expected_version: row.value_version }) } });
      onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const label = text(row.label, row.parameter_id);
  return (
    <div className="ay-tenant__panel">
      {row.description !== undefined && <p className="ay-tenant__description">{forUsers(text(row.description))}</p>}
      <dl className="ay-tenant__facts">
        <dt>Valeur actuelle</dt>
        <dd>
          <span className="ay-tenant__value">{shown(row.value, row.unit, row.enum_labels)}</span> {row.is_default ? <Pill>Par défaut</Pill> : <Pill tone="warn">Modifié</Pill>}
        </dd>
        <dt>Défaut</dt>
        <dd>{shown(row.default, row.unit, row.enum_labels)}</dd>
        {(row.min !== undefined || row.max !== undefined) && (
          <>
            <dt>Bornes</dt>
            <dd>{`${row.min ?? '…'} à ${row.max ?? '…'}${row.unit === undefined ? '' : ` ${row.unit}`}`}</dd>
          </>
        )}
        <dt>Déclaré par</dt>
        <dd>{layer}</dd>
      </dl>
      <form
        className="ay-tenant__inlineform"
        aria-label={`Modifier ${label}`}
        onSubmit={(event) => {
          event.preventDefault();
          void send(parsed(row, input, list));
        }}
      >
        {row.value_type === 'enum_list' ? (
          <fieldset className="ay-tenant__fieldset">
            <legend>Nouvelle valeur</legend>
            <div className="ay-tenant__chips">
              {(row.enum_values ?? []).map((option) => (
                <label key={option} className="ay-tenant__chipcheck">
                  <input type="checkbox" checked={list.includes(option)} onChange={() => setList(list.includes(option) ? list.filter((entry) => entry !== option) : [...list, option])} />
                  {shown(option, undefined, row.enum_labels)}
                </label>
              ))}
            </div>
          </fieldset>
        ) : (
          <label className="ay-field">
            <span className="ay-field__label">Nouvelle valeur{row.unit === undefined ? '' : ` (${row.unit})`}</span>
            {row.value_type === 'enum' || row.value_type === 'boolean' ? (
              <select value={input} onChange={(event) => setInput(event.target.value)}>
                {(row.value_type === 'boolean' ? ['true', 'false'] : (row.enum_values ?? [])).map((option) => (
                  <option key={option} value={option}>
                    {row.value_type === 'boolean' ? (option === 'true' ? 'oui' : 'non') : shown(option, undefined, row.enum_labels)}
                  </option>
                ))}
              </select>
            ) : (
              <input inputMode={row.value_type === 'string' ? 'text' : 'decimal'} value={input} onChange={(event) => setInput(event.target.value)} />
            )}
          </label>
        )}
        <label className="ay-field">
          <span className="ay-field__label">Motif</span>
          <input value={reason} required placeholder="Pourquoi ce changement ?" onChange={(event) => setReason(event.target.value)} />
        </label>
        <CommandError error={error} />
        <div className="ay-tenant__actions">
          {!row.is_default && (
            <button type="button" className="ay-button ay-button--ghost" disabled={busy || reason.trim() === ''} onClick={() => void send(row.default)}>
              Rétablir le défaut
            </button>
          )}
          <button type="button" className="ay-button ay-button--ghost" onClick={onCancel}>
            Annuler
          </button>
          <button type="submit" className="ay-button ay-button--primary" disabled={busy || reason.trim() === ''}>
            Enregistrer
          </button>
        </div>
      </form>
      <section aria-label={`Historique de ${label}`}>
        <h3 className="ay-tenant__panelhead">Historique</h3>
        {row.value_version === 0 ? <p className="ay-text ay-text--muted">Jamais modifié : la valeur par défaut s’applique.</p> : <ParameterHistory row={row} />}
      </section>
    </div>
  );
}

function ParameterHistory({ row }: { row: ParameterRow }) {
  const services = useAppServices();
  const [history] = useLoad<NonNullable<ParameterRow['history']>>(`parameter-history:${row.parameter_id}:${row.value_version}`, async () => (await services.client.query<{ parameters: ParameterRow[] }>('GetTenantParameters', { parameter_id: row.parameter_id })).data.parameters[0]?.history ?? []);
  if (history.status === 'loading') return <Loading />;
  if (history.status === 'error') return <>{errorView(history.error)}</>;
  return (
    <ol className="ay-tenant__history">
      {[...history.value].reverse().map((entry) => (
        <li key={entry.value_version}>
          <span className="ay-tenant__mono">v{entry.value_version}</span> {shown(entry.value, row.unit, row.enum_labels)} <span className="ay-text--muted">(avant : {shown(entry.previous_value, row.unit, row.enum_labels)})</span> — {entry.reason} — {new Date(entry.occurred_at).toLocaleString('fr-FR')}
        </li>
      ))}
    </ol>
  );
}
